<script setup lang="ts">
import { computed, shallowRef } from 'vue'
import { useRouter } from 'vue-router'

import AppScreen from '@/components/layout/AppScreen.vue'
import ScreenTabs from '@/components/layout/ScreenTabs.vue'
import MapControls from '@/components/map/MapControls.vue'
import type { MapMode } from '@/components/map/mapMode'
import MapView from '@/components/map/MapView.vue'
import StopDetailsDialog from '@/components/transit/StopDetailsDialog.vue'
import StopSearchField from '@/components/transit/StopSearchField.vue'
import { useMapControls } from '@/composables/useMapControls'
import { routeCodesAtStop, useTransitIndex } from '@/composables/useTransitIndex'
import { STOP_ZOOM_FLOOR, useVisibleStops } from '@/composables/useVisibleStops'
import type { Stop } from '@/types'
import { compareRouteCodes } from '@/utils/routeOrder'

import { HOME_TABS } from './tabs'

const router = useRouter()
const { index } = useTransitIndex()
const {
  basemap,
  toggleBasemap,
  location: userLocation,
  isLocating,
  error: locationError,
  locate,
} = useMapControls()
const { stops, belowZoomFloor, capped, onViewportChange } = useVisibleStops(index)

const allStops = computed(() => index.value?.allStops ?? [])

const dialogOpen = shallowRef(false)
const dialogStop = shallowRef<Stop>()

const dialogRouteCodes = computed(() => {
  const stopId = dialogStop.value?.id
  if (stopId === undefined) return []
  const entries = index.value?.routesByStopId.get(stopId)
  return entries ? [...routeCodesAtStop(entries)].sort(compareRouteCodes) : []
})

const mode = computed<MapMode>(() => ({
  kind: 'stops',
  stops: stops.value,
}))

function inspectStop(stopId: number) {
  const stop = index.value?.stopsById.get(stopId)
  if (!stop) return
  dialogStop.value = stop
  dialogOpen.value = true
}

function openStop(stopId: number) {
  dialogOpen.value = false
  router.push({ name: 'stop-buses', params: { stopId: String(stopId) } })
}
</script>

<template>
  <AppScreen title="Hanoi Bus" leading="menu">
    <template #tabs>
      <ScreenTabs :tabs="HOME_TABS" />
    </template>

    <div class="relative h-full">
      <MapView
        :mode="mode"
        :basemap="basemap"
        :user-location="userLocation"
        @select-stop="inspectStop"
        @viewport-change="onViewportChange"
      />

      <StopDetailsDialog
        v-model:open="dialogOpen"
        :stop="dialogStop"
        :route-codes="dialogRouteCodes"
        @view-buses="openStop"
      />

      <!-- Overlays are `absolute`, scoped to the map container, which itself
           sits inside the phone frame. Nothing here may be `fixed`. -->
      <div class="pointer-events-none absolute inset-x-0 top-0 z-10 p-3">
        <!-- `restore-id` opts this field into per-history-entry text memory: this
             screen has no scroll container, so `useScrollRestore` cannot carry it. -->
        <StopSearchField
          class="pointer-events-auto"
          :stops="allStops"
          restore-id="home-search"
          @select="openStop"
        />
      </div>

      <MapControls
        :basemap="basemap"
        :is-locating="isLocating"
        :error="locationError"
        @toggle-basemap="toggleBasemap"
        @locate="locate"
      >
        <p
          v-if="belowZoomFloor"
          class="pointer-events-auto bg-background px-2 py-1 text-xs text-muted-foreground ring-1 ring-foreground/25"
        >
          Zoom in to level {{ STOP_ZOOM_FLOOR }} to see stops
        </p>
        <!-- Silent truncation reads as "that stop doesn't exist". Say it. -->
        <p
          v-else-if="capped"
          class="pointer-events-auto bg-background px-2 py-1 text-xs text-muted-foreground ring-1 ring-foreground/25"
        >
          Showing the nearest {{ stops.length }} stops — zoom in for the rest
        </p>
      </MapControls>
    </div>
  </AppScreen>
</template>
